import { campaignRouteNames } from '@/app/campaigns/constants';
import { COMMUNITY_ROUTE_NAMES } from '@/app/community/constants';
import { DASHBOARD_ROUTE_NAMES } from '@/app/dashboards/constants';
import { SOCIAL_LISTENING_ROUTE_NAMES } from '@/app/socialListening/constants';
import { PRODUCT_MAP } from '@/models/auth/customer-plan.enum';
import { colours } from '@/ux/colours';

export const INTENT_TYPES = {
  SAMPLE: 'SAMPLE',
  METRIC_ANALYTICS: 'METRIC_ANALYTICS',
  UNIFIED: 'UNIFIED',
  DASHBOARD_REPORTS: 'DASHBOARD_REPORTS',
  TRENDS: 'TRENDS',
  TOPICS: 'TOPICS',
  COMMENTS: 'COMMENTS',
  DMS: 'DMS',
};

// UNIFIED is intentionally NOT included here — it is a conversational intent type.
// Adding it here causes loading state and chat history issues.
export const SUMMARY_INTENT_TYPES = [
  INTENT_TYPES.COMMENTS,
  INTENT_TYPES.DASHBOARD_REPORTS,
  INTENT_TYPES.DMS,
  INTENT_TYPES.TOPICS,
  INTENT_TYPES.TRENDS,
];

// some report components used to display message additional content rely on snake case fields
export const SKIP_CAMELIZE_CONTENT_INTENT_TYPES = [
  INTENT_TYPES.DASHBOARD_REPORTS,
  INTENT_TYPES.METRIC_ANALYTICS,
  INTENT_TYPES.UNIFIED,
];

export const ACTION_TYPES = {
  PROMPT: 'PROMPT',
  QUESTION: 'QUESTION',
};

export const VISION_AI_SOCKET_EVENTS = {
  VISION_AI_MESSAGE_CREATED: 'VISION_AI_MESSAGE_CREATED',
  VISION_AI_CLEAR_MESSAGES: 'VISION_AI_CLEAR_MESSAGES',
  VISION_AI_CONVERSATION_UPDATED: 'VISION_AI_CONVERSATION_UPDATED',
  VISION_AI_CONVERSATION_CREATED: 'VISION_AI_CONVERSATION_CREATED',
  VISION_AI_CONVERSATIONS_DELETED: 'VISION_AI_CONVERSATIONS_DELETED',
};

export const VISION_AI_MIXPANEL_EVENTS = {
  AI_WIDGET_INTERACTION: 'AI Widget Interaction',
  AI_WIDGET_LAUNCH: 'AI Widget Launch',
  AI_MESSAGE_INTERACTION: 'AI Message Interaction',
  AI_SUMMARY_LAUNCH: 'AI Summary Launch',
  USER_MESSAGE_SENT: 'User Message Sent',
  ASSISTANT_MESSAGE_RECEIVED_USER_FEEDBACK: 'Assistant Message Received - User Feedback',
  AI_INSIGHT_SURFACED: 'AI Insight Surfaced',
  AI_INSIGHT_CLICKED: 'AI Insight Clicked',
  AI_INSIGHT_OPENED: 'AI Insight Opened',
  AI_INSIGHT_CLOSED: 'AI Insight Closed',
};

/** Side panel type for the modernized Vision AI chat. Matches the `ChatAgentSidePanel` default.
 * This type is added src/components/side-panel/types.ts in Dashing UI and should be used
 * once it is released.
 *
 */
export const VISION_AI_PANEL_TYPE = 'vision-chat';
export const VISION_PANEL_WIDTH_EXPANDED = 1088;

export const VISION_AI_WIDGET_INTERACTIONS = {
  DELETE_ALL_HISTORY: 'Delete All History',
  DELETE_ICON_CLICKED: 'Delete Icon Clicked',
  DELETE_SINGLE_CHAT: 'Delete Single Chat',
  DELETE_SUMMARIES: 'Delete Summaries',
  EXPAND: 'Expand',
  HELP: 'Help',
  HIDE_CHAT_HISTORY: 'Hide Chat History',
  LOAD_HISTORY: 'Load History',
  NEW_CHAT: 'New Chat',
  RENAME_CHAT: 'Rename Chat',
  SHOW_CHAT_HISTORY: 'Show Chat History',
  SHRINK: 'Shrink',
  VIEW_CHAT: 'View Chat',
};

export const VISION_AI_WIDGET_LAUNCH_INTERACTIONS = {
  OPEN: 'Open',
  CLOSE: 'Close',
};

export const VISION_AI_SUMMARY_INTERACTIONS = {
  REGENERATE: 'Regenerate',
  COPY: 'Copy',
  THUMBS_UP: 'Thumbs Up',
  THUMBS_DOWN: 'Thumbs Down',
  DOWNLOAD_MEDIA: 'Download Media',
  SOURCE_LINK: 'Source Link',
  COMMENT_CLICK: 'Comment Click',
  POST_CLICK: 'Post Click',
  WEB_CLICK: 'Web Click',
  RESOURCE_CENTRE_CLICK: 'Resource Centre Click',
  CUSTOMER_SUPPORT: 'CX',
  REDIRECT_BUTTON_CLICK: 'Redirect Button Click',
  CANNED_PROMPT_CLICK: 'Canned Prompt Click',
};

export const VISION_AI_INTENT_TYPE_NAMES = {
  [INTENT_TYPES.METRIC_ANALYTICS]: 'Metric Analytics',
  [INTENT_TYPES.UNIFIED]: 'Unified',
  [INTENT_TYPES.DASHBOARD_REPORTS]: 'Dashboard',
  [INTENT_TYPES.COMMENTS]: 'Community Comment',
  [INTENT_TYPES.DMS]: 'Community DM',
  [INTENT_TYPES.TOPICS]: 'Social Listening Topic',
  [INTENT_TYPES.TRENDS]: 'Social Listening Trend',
};

export const VISION_AI_SUMMARY_LAUNCH_TYPE = {
  ICON: 'Icon',
  PROMPT: 'Prompt',
};

export const PNG_DOWNLOAD_TOOLTIP = 'Download as .png';

export const OLD_VISION_AI_RESOURCE_CENTER_URL =
  'https://help.dashsocial.com/hc/en-us/articles/25549170222477';

export const VISION_AI_RESOURCE_CENTER_URL =
  'https://help.dashsocial.com/hc/en-us/articles/29038584328973';

export const NO_ACCESS_MESSAGES = {
  SUMMARY_NOT_AVAILABLE: 'Vision AI Summaries are not available for this page yet.',
};

export const INTENT_TYPES_BY_ROUTE_NAME = {
  [DASHBOARD_ROUTE_NAMES.ID]: INTENT_TYPES.DASHBOARD_REPORTS,
  [DASHBOARD_ROUTE_NAMES.DASHBOARDS]: INTENT_TYPES.DASHBOARD_REPORTS,
  [SOCIAL_LISTENING_ROUTE_NAMES.TOPICS]: INTENT_TYPES.TOPICS,
  [SOCIAL_LISTENING_ROUTE_NAMES.TRENDS]: INTENT_TYPES.TRENDS,
  [campaignRouteNames.CAMPAIGN_DETAIL_OWNED_SUMMARY]: INTENT_TYPES.UNIFIED,
};

export const DEFAULT_METRIC_AND_ANALYTICS_PROMPTS = [
  'What was my Average Engagement Rate for Facebook and Instagram last week?',
  'What is my total net new followers for all channels between January and today?',
  'How many Video Views did I receive on TikTok last month?',
];

export const DEFAULT_METRIC_AND_ANALYTICS_PROMPTS_TOOLTIP =
  'Click this prompt to ask Vision AI to analyze your data';

export const DEFAULT_SUMMARY_PROMPTS_BY_INTENT_TYPE = {
  [INTENT_TYPES.DASHBOARD_REPORTS]: 'Summarize this Dashboard',
  [INTENT_TYPES.SAMPLE]: 'Send the sample message',
  [INTENT_TYPES.TOPICS]: 'Summarize this Topic',
  [INTENT_TYPES.TRENDS]: 'Summarize Trends',
  // DM_REFACTOR_2026: Once FF is lifted, rename to "Summarize Thread"
  [INTENT_TYPES.DMS]: 'Summarize DMs',
  [INTENT_TYPES.COMMENTS]: 'Summarize Comments',
};

export const COMMON_ICON_PROPS = {
  iconColor: colours.TEXT.TEXT_PRIMARY,
  dismiss: true,
  round: true,
};

export const VALID_DASHBOARD_REPORTS = {
  SINGLE_METRIC: 'SINGLE_METRIC',
  TOTAL_METRIC: 'TOTAL_METRIC',
  MULTI_BRAND_METRIC: 'MULTI_BRAND_METRIC',
  MULTI_METRIC_MEDIA_TYPE: 'MULTI_METRIC_MEDIA_TYPE',
  TOTAL_GROUPED_METRIC: 'TOTAL_GROUPED_METRIC',
  COMPETITIVE_MULTI_BRAND_METRIC: 'COMPETITIVE_MULTI_BRAND_METRIC',
  GRAPH: 'GRAPH',
  COMPETITIVE_GRAPH: 'COMPETITIVE_GRAPH',
};

export const THRESHOLD_MINIMUMS = {
  COMMUNITY: {
    COMMENTS: 3,
    DMS: 5,
  },
  LISTENING: {
    TRENDS_TOP_POSTS: 3,
    TOPICS_TOP_POSTS: 3,
  },
  DASHBOARDS: 3,
};

export const REGENERATE_TOOLTIPS = {
  LOADING: 'Vision AI is generating a response',
  READY: 'Regenerate response',
  THUMBS_UP: 'Good response',
  THUMBS_DOWN: 'Bad response',
};

export const USER_FEEDBACK = {
  POSITIVE: 'POSITIVE',
  NEGATIVE: 'NEGATIVE',
};

export const VISIT_RESOURCE_CENTER = `For more information, visit the `;
export const COMMUNITY_VISION_AI_TOOLTIPS = {
  COMMENTS: {
    ENABLED: 'Use Vision AI to summarize the comments on this post',
    DISABLED: `You need a minimum of ${THRESHOLD_MINIMUMS.COMMUNITY.COMMENTS} eligible comments to use AI Summaries. `,
  },
  DMS: {
    ENABLED: 'Use Vision AI to summarize this conversation',
    DISABLED: `You need a minimum of ${THRESHOLD_MINIMUMS.COMMUNITY.DMS} eligible messages to use AI Summaries. `,
  },
  EMPTY_STATE: {
    DISABLED: 'Vision AI Summaries are only available for DMs and Comments in the Community Inbox',
  },
};
export const SOCIAL_LISTENING_VISION_AI_TOOLTIPS = {
  TRENDS: {
    ENABLED: 'Use Vision AI to summarize Social Trends',
    DISABLED: `You need a minimum of ${THRESHOLD_MINIMUMS.LISTENING.TRENDS_TOP_POSTS} eligible posts on this page to use AI Summaries. `,
  },
  TOPICS: {
    ENABLED: 'Use Vision AI to summarize this Topic',
    DISABLED: `You need a minimum of ${THRESHOLD_MINIMUMS.LISTENING.TOPICS_TOP_POSTS} eligible posts on this page to use AI Summaries. `,
  },
  EMPTY_STATE: {
    DISABLED: 'Vision AI Summaries are only available for Social Listening Trends and Topics.',
  },
};

export const DASHBOARD_SUMMARY_REPORT_MINIMUM_MESSAGE = `This Dashboard can’t be summarized. It needs to have a minimum of ${THRESHOLD_MINIMUMS.DASHBOARDS} eligible reports:`;
export const DASHBOARD_SUMMARY_REPORT_MINIMUM_MESSAGE_RULES = [
  'Reports must contain data',
  'Only line graphs and metric reports are currently supported',
  'Data must be for owned or competitive reports only',
  'Multi-brand reports must be aggregated by brand',
  'Multi-channel reports must be aggregated by channel',
];

const bulletPoints = DASHBOARD_SUMMARY_REPORT_MINIMUM_MESSAGE_RULES.map(
  (rule) => `   <li class="list-item">    ${rule}</li>`,
).join('');

export const FORMATTED_DASHBOARD_SUMMARY_REPORT_MINIMUM_MESSAGE = `${DASHBOARD_SUMMARY_REPORT_MINIMUM_MESSAGE}<ul  class="indented-list"> ${bulletPoints}</ul>`;

export const DASHBOARD_SUMMARY_NO_DASHBOARDS_MESSAGE = `AI Summaries requires you to create a Dashboard first.`;

export const DASHBOARD_SUMMARIZE_BUTTON_TOOLTIP = 'Use Vision AI to summarize this Dashboard';

/*
error codes that may get set on assistant messages from the backend.
for runtime errors, messages can be attempted to be regenerated.
specify in config if any error codes should disallow message regeneration.
*/
export const ERROR_TYPES = {
  RUNTIME_ERROR: {
    value: 'RUNTIME_ERROR',
    statusCode: 500,
    errorMessage: 'Oops! There was an issue generating a response. Please try again.',
  },
  // error message unique per intent type
  UNAUTHORIZED_ERROR: {
    value: 'UNAUTHORIZED_ERROR',
    statusCode: 403,
    disallowMessageRegeneration: true,
  },
};

export const BACKUP_PROMPT_MESSAGE = 'Summarize';

export const UPGRADE_DASHBOARDS_CTA_CONFIG = {
  badge: 'Add-On',
  bulletPoints: [
    'Vision AI Summaries help you spot performance trends and create report-ready analytics digests',
    'Available with Premium Analytics add-on',
  ],
  cta: 'Try Premium Analytics',
  trialProduct: PRODUCT_MAP.PREMIUM_ANALYTICS,
  mixpanelEventName: 'Upgrade Options - Button Clicked',
  title: 'Summarize your Dashboards in seconds',
};

export const UPGRADE_CONVERSATIONAL_DASHBOARDS_CTA_CONFIG = {
  badge: 'Add-On',
  bulletPoints: [
    'Vision AI helps you spot performance trends and create report-ready analytics digests',
    'Available with the Premium Analytics add-on',
  ],
  cta: 'Try Premium Analytics',
  trialProduct: PRODUCT_MAP.PREMIUM_ANALYTICS,
  mixpanelEventName: 'Upgrade Options - Button Clicked',
  title: 'Understand your data in seconds',
};

export const UPGRADE_COMMUNITY_CTA_CONFIG = {
  badge: 'Upgrade',
  bulletPoints: [
    'Vision AI Summaries identify themes and provide quick insights into how your audience is engaging with your posts',
    'Available in the Advance plan',
  ],
  cta: 'Try Advance',
  trialProduct: PRODUCT_MAP.ADVANCE,
  mixpanelEventName: 'Upgrade Options - Button Clicked',
  title: 'Summarize your messages in seconds',
};

export const LISTENING_SEARCH_LIMIT = 25;

export const CONVERSATION_SETTING_ACTIONS = {
  RENAME: 'Rename',
  DELETE: 'Delete',
};

export const CONVERSATION_TEXT_BOX_WARNING =
  'When asking Vision AI about your social media performance, try to be clear and specific for the best results';

export const NEW_CHAT_TOOLTIP = {
  ENABLED: 'New Chat',
  DISABLED: 'You are already in a new chat',
  UPGRADE:
    'Chats with Vision AI are available with Premium Analytics. Upgrade to ask additional questions and explore deeper insights.',
};

export const TEXTBOX_UPGRADE_TOOLTIP =
  'Follow-up questions are available with Premium Analytics. Upgrade to ask additional questions and explore deeper insights.';

export const PROMPT_BUTTON_UPGRADE_TOOLTIP =
  'Prompts are available with Premium Analytics. Upgrade to ask additional questions and explore deeper insights.';

export const OLD_INTENT_TOOLTIP =
  "Vision has been improved and can now answer more of your questions. This conversation is from an earlier version and can't be continued. To access the latest features, please start a new conversation.";

export const ELEMENTS_TO_IGNORE_WHEN_CLOSING_POPUP = [
  '[data-test="see-more-content-popup"]',
  '[data-test="media-popup"]',
  '[data-test="DropdownButton-Item"]',
  '[data-test="popover-dropdown-button"]',
  '[data-test="rename-conversation-popup"]',
  '[data-test="ConfirmDialog"]',
  '[data-test="ConfirmationDialog"]',
  '[data-confirmation-dialog-mask]',
  '[data-test="vision-ai-button"]',
];

export const TEXTBOX_PLACEHOLDERS = {
  DEFAULT: ' Ask Vision AI about your analytics data',
  SUMMARIES: ' Start a new chat about your analytics data',
};

export const VISION_AI_MOCK_PROMPT_ROUTES = [
  COMMUNITY_ROUTE_NAMES.INSIGHTS,
  SOCIAL_LISTENING_ROUTE_NAMES.OVERVIEW,
];

export const CLEAR_CHAT_HISTORY_CONFIRMATION_MESSAGE =
  'This will erase your entire chat history. Are you sure you want to proceed?';

export const DELETE_CONVERSATION_CONFIRMATION_MESSAGE =
  'This will permanently delete it from your history. Are you sure you want to proceed?';

export const PROGRESSIVE_UPDATE_MESSAGES = {
  START: [
    ' Thinking through your request',
    ' Checking which metrics are most relevant',
    ' Confirming timeframe and channels',
    ' Pulling in the latest data',
  ],
  PROCESSING: [
    ' Looking at performance details',
    ' Checking across channels and sources',
    ' Organizing the data for consistency',
    ' Filtering out anything unusual',
    ' Spotting key patterns in the numbers',
    " Identifying what's standing out",
    ' Reviewing how results change over time',
    ' Comparing different parts of your data',
    ' Looking for unexpected changes',
    ' Digging deeper into audience signals',
    ' Summarizing recurring trends',
    ' Highlighting important shifts',
    ' Focusing on the biggest movers',
    ' Pinpointing what might be driving results',
    ' Making sure the data looks complete',
    ' Double-checking calculations',
    ' Prioritizing the most important insights',
    ' Drafting a clear set of takeaways',
  ],
  PACKAGING: [
    ' Preparing results for the summary',
    ' Packaging findings into a clear summary',
    ' Getting everything ready to share',
  ],
};

export const REQUEST_CONTEXT_BUTTON_IDENTIFIERS = {
  SUMMARIZE_COMMUNITY_MESSAGES: 'SUMMARIZE_COMMUNITY_MESSAGES',
  SUMMARIZE_COMMUNITY_INSIGHTS: 'SUMMARIZE_COMMUNITY_INSIGHTS',
  SUMMARIZE_COMMENT_THREAD: 'SUMMARIZE_COMMENT_THREAD',
  SUMMARIZE_DM_THREAD: 'SUMMARIZE_DM_THREAD',
  SUMMARIZE_CAMPAIGN_COMMENTS: 'SUMMARIZE_CAMPAIGN_COMMENTS',
  VIEW_INSIGHT: 'VIEW_INSIGHT',
};
